import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/apiError";

/**
 * Google OAuth callback landing page — Google redirects here with ?code=...
 * We POST the code to /api/auth/google/callback, backend exchanges for tokens
 * and issues a session cookie. Then we bounce to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against React 18 StrictMode double-invoke
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const error = params.get("error");

    if (error) {
      toast.error(`Google sign-in cancelled: ${error}`);
      navigate("/login", { replace: true });
      return;
    }
    if (!code) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        // redirect_uri MUST match what was sent to Google in the auth request.
        // window.location.origin ensures preview vs. prod alignment.
        await api.post("/auth/google/callback", {
          code,
          redirect_uri: `${window.location.origin}/auth/google`,
        });
        await checkAuth();
        toast.success("Signed in");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        toast.error(extractErrorMessage(e, "Google sign-in failed"));
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, checkAuth]);

  return (
    <div
      className="min-h-screen flex items-center justify-center text-sm"
      style={{ backgroundColor: "#050E0D", color: "rgba(255,255,255,0.65)" }}
      data-testid="google-callback-loading"
    >
      Completing sign-in…
    </div>
  );
}
